import { FormEvent, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createSupplier, type Supplier, type SupplierInput } from '@/lib/api/suppliers.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: (supplier: Supplier) => void;
};

export function NewSupplierModal({ open, onClose, onCreated }: Props) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [defaultLeadTimeDays, setDefaultLeadTimeDays] = useState('');
  const [notes, setNotes] = useState('');

  const mutation = useMutation({
    mutationFn: (body: SupplierInput) => createSupplier(body),
    onSuccess: (supplier) => {
      toastSuccess('Supplier created');
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      void queryClient.invalidateQueries({ queryKey: ['suppliers-active'] });
      onCreated?.(supplier);
      setName('');
      setContactName('');
      setContactEmail('');
      setContactPhone('');
      setWebsite('');
      setAccountNumber('');
      setPaymentTerms('');
      setDefaultLeadTimeDays('');
      setNotes('');
      onClose();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  if (!open) return null;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    mutation.mutate({
      name: name.trim(),
      contactName: contactName.trim() || null,
      contactEmail: contactEmail.trim() || null,
      contactPhone: contactPhone.trim() || null,
      website: website.trim() || null,
      accountNumber: accountNumber.trim() || null,
      paymentTerms: paymentTerms.trim() || null,
      defaultLeadTimeDays: defaultLeadTimeDays ? Number(defaultLeadTimeDays) : null,
      notes: notes.trim() || null,
      active: true,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-slate-950">New supplier</h2>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <label className="block text-sm">
            <span className="font-medium">Name *</span>
            <input
              className="mt-1 w-full rounded-xl border px-3 py-2"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="font-medium">Contact name</span>
              <input
                className="mt-1 w-full rounded-xl border px-3 py-2"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium">Phone</span>
              <input
                className="mt-1 w-full rounded-xl border px-3 py-2"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium">Email</span>
              <input
                type="email"
                className="mt-1 w-full rounded-xl border px-3 py-2"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium">Website</span>
              <input
                className="mt-1 w-full rounded-xl border px-3 py-2"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium">Account #</span>
              <input
                className="mt-1 w-full rounded-xl border px-3 py-2"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium">Payment terms</span>
              <input
                className="mt-1 w-full rounded-xl border px-3 py-2"
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                placeholder="Net 30"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium">Default lead time (days)</span>
              <input
                type="number"
                min={0}
                className="mt-1 w-full rounded-xl border px-3 py-2"
                value={defaultLeadTimeDays}
                onChange={(e) => setDefaultLeadTimeDays(e.target.value)}
              />
            </label>
          </div>
          <label className="block text-sm">
            <span className="font-medium">Notes</span>
            <textarea
              className="mt-1 w-full rounded-xl border px-3 py-2"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded-xl border px-4 py-2 text-sm font-semibold text-slate-700"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || !name.trim()}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving…' : 'Create supplier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
